import React, { useState, useEffect } from 'react';
import { bannerService } from '../../services/BannerService';
import { brandSectionService } from '../../services/BrandSectionService';
import { HeroBanner, BrandSection, DeviceFamily, Brand } from '../../types';
import { Plus, Trash2, Edit, Save, X, Image as ImageIcon, Loader2, ZoomIn, Move, LayoutGrid, Smartphone, Tablet, Monitor, Zap } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { compressImage, validateVideo } from '../../utils/imageOptimizer';
import { EditFamilyModal } from '../components/EditFamilyModal';
import { useToast } from '../../context/ToastContext';
import NarrativeSettings from '../components/NarrativeSettings';


// ... (rest of imports)

const ContentPage: React.FC = () => {
    const { showToast, showConfirm } = useToast();
    const [activeTab, setActiveTab] = useState<'banners' | 'sections' | 'families' | 'narrative'>('banners');

    // --- BANNERS STATE ---
    const [banners, setBanners] = useState<HeroBanner[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [currentBanner, setCurrentBanner] = useState<Partial<HeroBanner>>({});
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);

    // Banner Editing State
    const [scale, setScale] = useState(1.0);
    const [posX, setPosX] = useState(50);
    const [posY, setPosY] = useState(50);
    const [previewDevice, setPreviewDevice] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
    const [showSafeZones, setShowSafeZones] = useState(true);
    const [draftFraming, setDraftFraming] = useState<any>({});

    const changePreviewDevice = (newDevice: 'mobile' | 'tablet' | 'desktop') => {
        setDraftFraming((prev: any) => {
            const updated = {
                ...prev,
                [previewDevice]: { scale, x: posX, y: posY }
            };
            const nextDeviceSettings = updated[newDevice] || { scale: 1.0, x: 50, y: 50 };
            setScale(nextDeviceSettings.scale);
            setPosX(nextDeviceSettings.x);
            setPosY(nextDeviceSettings.y);
            return updated;
        });
        setPreviewDevice(newDevice);
    };

    // --- SECTIONS STATE ---
    const [sections, setSections] = useState<BrandSection[]>([]);
    const [isEditingSection, setIsEditingSection] = useState(false);
    const [currentSection, setCurrentSection] = useState<Partial<BrandSection>>({});
    const [sectionImageFile, setSectionImageFile] = useState<File | null>(null);
    const [currentSectionIndex, setCurrentSectionIndex] = useState<number>(0);
    const [showRealPreview, setShowRealPreview] = useState(true);
    const [showRealBannerPreview, setShowRealBannerPreview] = useState(true);

    // --- FAMILIES STATE ---
    const [families, setFamilies] = useState<DeviceFamily[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]); // Add brands state
    const [isEditingFamily, setIsEditingFamily] = useState(false);
    const [currentFamily, setCurrentFamily] = useState<Partial<DeviceFamily>>({});
    const [familyImageFile, setFamilyImageFile] = useState<File | null>(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setIsLoading(true);
        const [bannersData, sectionsData, { data: familiesData }, { data: brandsData }] = await Promise.all([
            bannerService.getAllAdmin(),
            brandSectionService.getAll(),
            supabase.from('device_families').select('*').order('brand_id', { ascending: true }).order('display_order', { ascending: true }),
            supabase.from('brands').select('*').order('name', { ascending: true }) // Fetch brands
        ]);
        setBanners(bannersData);
        setSections(sectionsData);
        if (familiesData) setFamilies(familiesData);
        if (brandsData) setBrands(brandsData);
        setIsLoading(false);
    };

    // ==========================================
    // BANNER HANDLERS
    // ==========================================

    const handleEdit = (banner: HeroBanner) => {
        setCurrentBanner(banner);
        setIsEditing(true);
        setImageFile(null);

        const savedFraming = banner.framing_settings || {};
        let initialFraming = {
            mobile: { scale: 1.0, x: 50, y: 50, ...(savedFraming.mobile || {}) },
            tablet: { scale: 1.0, x: 50, y: 50, ...(savedFraming.tablet || {}) },
            desktop: { scale: banner.scale || 1.0, x: 50, y: 50, ...(savedFraming.desktop || {}) }
        };

        if (banner.position && banner.position.includes('%') && !banner.framing_settings) {
            const [x, y] = banner.position.split(' ').map(p => parseFloat(p));
            initialFraming.desktop.x = isNaN(x) ? 50 : x;
            initialFraming.desktop.y = isNaN(y) ? 50 : y;
            initialFraming.mobile = { ...initialFraming.desktop };
            initialFraming.tablet = { ...initialFraming.desktop };
        }

        setDraftFraming(initialFraming);
        setPreviewDevice('desktop');
        setScale(initialFraming.desktop.scale);
        setPosX(initialFraming.desktop.x);
        setPosY(initialFraming.desktop.y);
    };

    const handleAddNew = () => {
        if (banners.length >= 6) {
            alert('Has alcanzado el límite máximo de 6 banners hero.');
            return;
        }
        setCurrentBanner({
            title: '',
            subtitle: '',
            position: '50% 50%',
            scale: 1.0,
            active: true,
            order: banners.length + 1
        });
        setIsEditing(true);
        setImageFile(null);

        const defaultFraming = {
            mobile: { scale: 1.0, x: 50, y: 50 },
            tablet: { scale: 1.0, x: 50, y: 50 },
            desktop: { scale: 1.0, x: 50, y: 50 }
        };
        setDraftFraming(defaultFraming);
        setPreviewDevice('desktop');
        setScale(1.0);
        setPosX(50);
        setPosY(50);
    };

    const handleSave = async () => {
        if (!currentBanner.image && !imageFile) {
            alert('La imagen/video es requerida');
            return;
        }

        setUploading(true);

        try {
            let imageUrl = currentBanner.image;

            if (imageFile) {
                let uploadFile = imageFile;

                // Validación para Imágenes
                if (imageFile.type.startsWith('image/')) {
                    try {
                        uploadFile = await compressImage(imageFile, { maxWidth: 1920, quality: 0.8 });
                    } catch (e: any) {
                        alert(e.message);
                        setUploading(false);
                        return;
                    }
                }
                // Validación para Videos
                else if (imageFile.type.startsWith('video/')) {
                    try {
                        await validateVideo(imageFile);
                        uploadFile = imageFile; // Videos no se comprimen en cliente, solo se validan
                    } catch (e: any) {
                        alert(e.message);
                        setUploading(false);
                        return;
                    }
                } else {
                    alert('Formato de archivo no soportado (solo imagen o video)');
                    setUploading(false);
                    return;
                }

                const fileExt = imageFile.name.split('.').pop();
                const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
                const filePath = `${fileName}`;

                const { error: uploadError } = await supabase.storage
                    .from('banners')
                    .upload(filePath, uploadFile);

                if (uploadError) throw uploadError;

                const { data: { publicUrl } } = supabase.storage
                    .from('banners')
                    .getPublicUrl(filePath);

                imageUrl = publicUrl;
            }

            const finalFraming = {
                ...draftFraming,
                [previewDevice]: { scale, x: posX, y: posY }
            };

            const bannerData = {
                ...currentBanner,
                title: currentBanner.title || '',
                subtitle: currentBanner.subtitle || '',
                position: `${finalFraming.desktop.x}% ${finalFraming.desktop.y}%`,
                scale: finalFraming.desktop.scale,
                framing_settings: finalFraming,
                image: imageUrl
            } as HeroBanner;

            if (currentBanner.id) {
                await bannerService.update(currentBanner.id, bannerData);
            } else {
                await bannerService.create(bannerData);
            }

            setIsEditing(false);
            loadData();
        } catch (error: any) {
            console.error('Error saving banner:', error);
            alert(error.message || 'Error al guardar el banner');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id: string) => {
        const confirmed = await showConfirm(
            'Eliminar Banner',
            '¿Estás seguro de eliminar este banner hero?',
            true
        );
        if (confirmed) {
            await bannerService.delete(id);
            loadData();
            showToast('Banner eliminado correctamente', 'success');
        }
    };

    // ==========================================
    // SECTION HANDLERS
    // ==========================================

    const handleEditSection = (section: BrandSection) => {
        setCurrentSection(section);
        setIsEditingSection(true);
        setSectionImageFile(null);

        const savedFraming = section.framing_settings || {};
        let initialFraming = {
            mobile: { scale: 1.0, x: 50, y: 50, ...(savedFraming.mobile || {}) },
            tablet: { scale: 1.0, x: 50, y: 50, ...(savedFraming.tablet || {}) },
            desktop: { scale: section.scale || 1.0, x: 50, y: 50, ...(savedFraming.desktop || {}) }
        };

        if (section.position && section.position.includes('%') && !section.framing_settings) {
            const [x, y] = section.position.split(' ').map(p => parseFloat(p));
            initialFraming.desktop.x = isNaN(x) ? 50 : x;
            initialFraming.desktop.y = isNaN(y) ? 50 : y;
            initialFraming.mobile = { ...initialFraming.desktop };
            initialFraming.tablet = { ...initialFraming.desktop };
        }

        setDraftFraming(initialFraming);
        setPreviewDevice('desktop');
        setScale(initialFraming.desktop.scale);
        setPosX(initialFraming.desktop.x);
        setPosY(initialFraming.desktop.y);

        // Track index for layout detection
        const idx = sections.findIndex(s => s.id === section.id);
        setCurrentSectionIndex(idx >= 0 ? idx : sections.length);
    };

    const handleAddNewSection = () => {
        setCurrentSection({
            title: '',
            subtitle: '',
            cta_text: 'Ver Catálogo',
            cta_link: '/catalogo',
            display_order: sections.length + 1,
            is_active: true,
            position: '50% 50%',
            scale: 1.0
        });
        setIsEditingSection(true);
        setSectionImageFile(null);
        const defaultFraming = {
            mobile: { scale: 1.0, x: 50, y: 50 },
            tablet: { scale: 1.0, x: 50, y: 50 },
            desktop: { scale: 1.0, x: 50, y: 50 }
        };
        setDraftFraming(defaultFraming);
        setPreviewDevice('desktop');
        setScale(1.0);
        setPosX(50); setPosY(50);
        setCurrentSectionIndex(sections.length);
    };

    const handleDeleteSection = async (id: string) => {
        const confirmed = await showConfirm(
            'Eliminar Sección',
            '¿Estás seguro de eliminar esta sección destacada?',
            true
        );
        if (confirmed) {
            await brandSectionService.delete(id);
            loadData();
            showToast('Sección eliminada correctamente', 'success');
        }
    };



    // ==========================================
    // FAMILY HANDLERS
    // ==========================================

    const handleEditFamily = (family: DeviceFamily) => {
        setCurrentFamily(family);
        setIsEditingFamily(true);
        setFamilyImageFile(null);
        // Reset zoom/pos for simplicity or load if we store it (we don't store pos/scale for families yet, but we can reuse the UI state for the session)
        setScale(1.0);
        setPosX(50); setPosY(50);
    };

    const handleSaveFamily = async (id: string | undefined, data: Partial<DeviceFamily>) => {
        try {
            if (id) {
                const { error } = await supabase
                    .from('device_families')
                    .update({
                        name: data.name,
                        image_url: data.image_url,
                        brand_id: data.brand_id,
                        display_order: data.display_order,
                        is_active: data.is_active,
                        slug: data.slug,
                        framing_settings: data.framing_settings
                    })
                    .eq('id', id);

                if (error) throw error;
            } else {
                const { error } = await supabase
                    .from('device_families')
                    .insert([{
                        name: data.name,
                        image_url: data.image_url,
                        brand_id: data.brand_id,
                        display_order: data.display_order,
                        is_active: data.is_active,
                        slug: data.slug,
                        framing_settings: data.framing_settings
                    }]);

                if (error) throw error;
            }

            setIsEditingFamily(false);
            loadData();
        } catch (error) {
            console.error('Error saving family:', error);
            throw error; // Let modal handle error toast
        }
    };

    const handleDeleteFamily = async (id: string) => {
        const confirmed = await showConfirm(
            'Eliminar Familia',
            '¿Estás seguro de eliminar esta familia?',
            false
        );

        if (confirmed) {
            const doubleConfirmed = await showConfirm(
                'ADVERTENCIA CRÍTICA',
                'Esta acción es irreversible y podría afectar a los productos vinculados. ¿Confirmas la eliminación definitiva?',
                true
            );

            if (doubleConfirmed) {
                try {
                    const { error } = await supabase
                        .from('device_families')
                        .delete()
                        .eq('id', id);

                    if (error) throw error;
                    showToast('Familia eliminada correctamente', 'success');
                    loadData();
                } catch (error: any) {
                    console.error('Error deleting family:', error);
                    showToast(error.message || 'Error al eliminar. Verifica si hay productos usando esta familia.', 'error');
                }
            }
        }
    };

    const handleSaveSection = async () => {
        if (!currentSection.title) {
            showToast('El título es requerido', 'error');
            return;
        }

        setUploading(true);
        try {
            let imageUrl = currentSection.image_url;

            if (sectionImageFile) {
                const uploadFile = await compressImage(sectionImageFile, { maxWidth: 1200, quality: 0.8 });
                const fileExt = sectionImageFile.name.split('.').pop();
                const fileName = `section-${currentSection.id || 'new'}-${Date.now()}.${fileExt}`;

                const { error: uploadError } = await supabase.storage
                    .from('sections')
                    .upload(fileName, uploadFile);

                if (uploadError) throw uploadError;

                const { data: { publicUrl } } = supabase.storage
                    .from('sections')
                    .getPublicUrl(fileName);

                imageUrl = publicUrl;
            }

            const finalFraming = {
                ...draftFraming,
                [previewDevice]: { scale, x: posX, y: posY }
            };

            const sectionData = {
                ...currentSection,
                title: currentSection.title,
                subtitle: currentSection.subtitle || '',
                image_url: imageUrl,
                cta_text: currentSection.cta_text || 'Ver Catálogo',
                cta_link: currentSection.cta_link || '/catalogo',
                position: `${finalFraming.desktop.x}% ${finalFraming.desktop.y}%`,
                scale: finalFraming.desktop.scale,
                framing_settings: finalFraming,
                is_active: currentSection.is_active ?? true,
                display_order: currentSection.display_order ?? 0
            };

            if (currentSection.id) {
                await brandSectionService.update(currentSection.id, sectionData as BrandSection);
                showToast('Sección actualizada correctamente', 'success');
            } else {
                await brandSectionService.create(sectionData);
                showToast('Sección creada correctamente', 'success');
            }

            setIsEditingSection(false);
            loadData();
        } catch (error) {
            console.error('Error saving section:', error);
            showToast('Error al guardar la sección', 'error');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="space-y-12 pb-20">
            {/* --- MODALS --- */}
            {isEditing && (
                <div
                    className="fixed inset-0 bg-black/70 backdrop-blur-3xl z-[99999] flex items-center justify-center p-4"
                    onClick={() => setIsEditing(false)}
                >
                    <div
                        className="bg-zinc-900 border border-zinc-800 w-full max-w-4xl rounded-lg shadow-2xl max-h-[90vh] flex flex-col relative z-[100000] overflow-hidden"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header Fixed */}
                        <div className="p-4 md:p-6 border-b border-zinc-800 flex justify-between items-center bg-zinc-900/95 backdrop-blur-sm z-10">
                            <h3 className="text-xl font-display font-bold uppercase italic text-white flex items-center gap-2">
                                <span className="text-white">{currentBanner.id ? 'EDITAR' : 'NUEVO'}</span> BANNER
                            </h3>
                            <button
                                onClick={() => setIsEditing(false)}
                                className="bg-zinc-800 hover:bg-white text-zinc-400 hover:text-black p-2 rounded-full transition-all border border-zinc-700 hover:border-white"
                                title="Cerrar"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-grow overflow-y-auto overflow-x-hidden p-6 space-y-6">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-[10px] font-bold uppercase text-zinc-500 mb-1 tracking-wider">Título</label>
                                    <input type="text" value={currentBanner.title} onChange={e => setCurrentBanner({ ...currentBanner, title: e.target.value })} className="w-full bg-black border border-zinc-800 focus:border-white outline-none p-3 text-white rounded-sm transition-colors" placeholder="Ej: NUEVA COLECCIÓN" />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold uppercase text-zinc-500 mb-1 tracking-wider">Subtítulo</label>
                                    <input type="text" value={currentBanner.subtitle} onChange={e => setCurrentBanner({ ...currentBanner, subtitle: e.target.value })} className="w-full bg-black border border-zinc-800 focus:border-white outline-none p-3 text-white rounded-sm transition-colors" placeholder="Ej: Fibra de carbono real" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div><label className="block text-[10px] font-bold uppercase text-zinc-500 mb-1 tracking-wider">Orden</label><input type="number" value={currentBanner.order} onChange={e => setCurrentBanner({ ...currentBanner, order: parseInt(e.target.value) })} className="w-full bg-black border border-zinc-800 focus:border-white outline-none p-3 text-white rounded-sm transition-colors" /></div>
                                    <div><label className="block text-[10px] font-bold uppercase text-zinc-500 mb-1 tracking-wider">Visibilidad</label><select value={currentBanner.active ? 'true' : 'false'} onChange={e => setCurrentBanner({ ...currentBanner, active: e.target.value === 'true' })} className="w-full bg-black border border-zinc-800 focus:border-white outline-none p-3 text-white rounded-sm transition-colors"><option value="true">Activo</option><option value="false">Oculto</option></select></div>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold uppercase text-zinc-500 mb-1 tracking-wider">Multimedia (Imagen/Video)</label>
                                    <div className="flex flex-col gap-2">
                                        <input type="file" accept="image/*,video/*" onChange={e => e.target.files && setImageFile(e.target.files[0])} className="w-full bg-zinc-950 border border-zinc-800 p-2 text-xs text-zinc-400 file:bg-zinc-800 file:border-0 file:text-white file:px-3 file:py-1 file:rounded-sm file:mr-3 file:cursor-pointer" />

                                        {(currentBanner.image || imageFile) && (
                                            <div className="space-y-4">
                                                <div className="flex flex-wrap bg-black/40 p-1 rounded-lg border border-zinc-800 self-center justify-center gap-1 items-center">
                                                    {[
                                                        { id: 'mobile', icon: Smartphone, label: 'Móvil' },
                                                        { id: 'tablet', icon: Tablet, label: 'Tablet' },
                                                        { id: 'desktop', icon: Monitor, label: 'Escritorio' }
                                                    ].map((device) => (
                                                        <button
                                                            key={device.id}
                                                            onClick={() => changePreviewDevice(device.id as any)}
                                                            className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${previewDevice === device.id
                                                                ? 'bg-white text-black shadow-lg shadow-white/10'
                                                                : 'text-zinc-500 hover:text-white hover:bg-zinc-800'
                                                                }`}
                                                        >
                                                            <device.icon size={14} />
                                                            <span className="hidden sm:inline">{device.label}</span>
                                                        </button>
                                                    ))}
                                                    <div className="w-[1px] h-4 bg-zinc-800 mx-0.5 hidden sm:block" />
                                                    <button
                                                        onClick={() => setShowSafeZones(!showSafeZones)}
                                                        className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${showSafeZones
                                                            ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                                            : 'text-zinc-500 hover:text-white hover:bg-zinc-800'
                                                            }`}
                                                        title="Mostrar/Ocultar Zonas Seguras"
                                                    >
                                                        <LayoutGrid size={14} />
                                                        <span className="hidden sm:inline">Guías</span>
                                                    </button>
                                                    <button
                                                        onClick={() => setShowRealBannerPreview(!showRealBannerPreview)}
                                                        className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${showRealBannerPreview
                                                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                                            : 'text-zinc-500 hover:text-white hover:bg-zinc-800'
                                                            }`}
                                                        title="Ver con textos reales"
                                                    >
                                                        <Zap size={14} />
                                                        <span className="hidden sm:inline">Real</span>
                                                    </button>
                                                </div>

                                                <div className="flex flex-col lg:flex-row gap-6 mt-4 items-start">
                                                    {/* Image Preview Side */}
                                                    <div className="w-full lg:w-2/3 flex flex-col space-y-2">
                                                        <div className={`relative rounded overflow-hidden border border-zinc-700 bg-black group/preview transition-all duration-300 w-full mx-auto ${previewDevice === 'mobile' ? 'aspect-[4/3] max-w-[300px]' :
                                                            previewDevice === 'tablet' ? 'aspect-[16/10] max-w-[500px]' :
                                                                'aspect-[21/9] max-w-[600px]'
                                                            }`}>
                                                            {imageFile?.type.startsWith('video/') || (currentBanner.image && currentBanner.image.match(/\.(mp4|webm|ogg)$/i)) ? (
                                                                <video
                                                                    src={imageFile ? URL.createObjectURL(imageFile) : currentBanner.image}
                                                                    className="w-full h-full object-cover transition-all duration-500"
                                                                    style={{
                                                                        objectPosition: `${posX}% ${posY}%`,
                                                                        transform: `scale(${scale})`,
                                                                        transformOrigin: `${posX}% ${posY}%`
                                                                    }}
                                                                    muted loop autoPlay
                                                                />
                                                            ) : (
                                                                <img
                                                                    src={imageFile ? URL.createObjectURL(imageFile) : currentBanner.image}
                                                                    className="w-full h-full object-cover transition-all duration-500"
                                                                    style={{
                                                                        objectPosition: `${posX}% ${posY}%`,
                                                                        transform: `scale(${scale})`,
                                                                        transformOrigin: `${posX}% ${posY}%`
                                                                    }}
                                                                />
                                                            )}

                                                            {/* SAFE ZONES OVERLAY */}
                                                            {showSafeZones && (
                                                                <div className="absolute inset-0 pointer-events-none z-10">
                                                                    {/* Text Area (Left/Center) */}
                                                                    <div className="absolute left-8 bottom-[15%] w-[60%] h-[40%] border border-white/60 border-dashed rounded flex flex-col justify-end p-2">
                                                                        <span className="text-[8px] text-white/80 uppercase font-black italic">Zona Texto</span>
                                                                    </div>

                                                                    {/* Navigation Dots Area */}
                                                                    <div className="absolute inset-x-0 bottom-0 h-10 border-t border-red-500/40 bg-red-500/15 flex items-center justify-center">
                                                                        <span className="text-[8px] text-red-500/80 uppercase font-black italic">Zona Navegación</span>
                                                                    </div>

                                                                    {/* Horizontal Safe Margin */}
                                                                    <div className="absolute inset-y-0 left-0 w-8 border-r border-white/30 bg-white/15" />
                                                                    <div className="absolute inset-y-0 right-0 w-8 border-l border-white/30 bg-white/15" />

                                                                    {/* Crosshair */}
                                                                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-[1px] bg-white/60" />
                                                                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-4 w-[1px] bg-white/60" />
                                                                </div>
                                                            )}

                                                            {/* REAL PREVIEW OVERLAY */}
                                                            {showRealBannerPreview && (
                                                                <div className="absolute inset-0 pointer-events-none z-20 overflow-hidden text-left">
                                                                    {/* Gradient Overlay */}
                                                                    <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent" />

                                                                    {/* Content */}
                                                                    <div className="absolute inset-0 flex items-center justify-center">
                                                                        <div className="w-full px-8">
                                                                            <div className="max-w-2xl text-left">
                                                                                <h2 className="text-xl md:text-4xl font-display font-black text-white italic uppercase tracking-tighter mb-1 drop-shadow-lg leading-none">
                                                                                    {currentBanner.title || 'TÍTULO DEL BANNER'}
                                                                                </h2>
                                                                                <p className="text-[10px] md:text-base text-gray-200 font-medium drop-shadow-md border-l-2 border-white pl-4">
                                                                                    {currentBanner.subtitle || 'Subtítulo descriptivo aquí'}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {/* Navigation Dots Simulation */}
                                                                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex space-x-1.5 z-10">
                                                                        <div className="w-4 h-1.5 rounded-full bg-white" />
                                                                        <div className="w-1.5 h-1.5 rounded-full bg-white/50" />
                                                                        <div className="w-1.5 h-1.5 rounded-full bg-white/50" />
                                                                    </div>
                                                                </div>
                                                            )}

                                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/preview:opacity-100 transition-opacity flex items-center justify-center">
                                                                <span className="text-[10px] font-bold uppercase tracking-widest bg-black/60 px-3 py-1 rounded">Vista Previa {previewDevice}</span>
                                                            </div>
                                                        </div>
                                                        <p className="text-[9px] text-zinc-500 text-center italic">
                                                            {showSafeZones
                                                                ? "* Las áreas marcadas indican donde el texto o puntos de navegación podrían tapar tu imagen."
                                                                : "* Simulación del recorte real. Usa los controles a la derecha para encuadrar."
                                                            }
                                                        </p>
                                                    </div>

                                                    {/* Controls Side */}
                                                    <div className="w-full lg:w-1/3 bg-zinc-950 p-4 rounded border border-zinc-800 space-y-4 shadow-inner shrink-0 sticky top-0">
                                                        <h4 className="text-[10px] font-bold uppercase text-zinc-500 mb-2 flex items-center gap-2">
                                                            <Move size={12} /> Ajustes de Encuadre
                                                        </h4>
                                                        <div className="space-y-1">
                                                            <div className="flex justify-between text-[10px] uppercase text-zinc-500"><span>Zoom</span><span className="text-white font-mono">{scale.toFixed(1)}x</span></div>
                                                            <input type="range" min="1" max="2" step="0.1" value={scale} onChange={(e) => setScale(parseFloat(e.target.value))} onDoubleClick={() => setScale(1.0)} className="w-full accent-white h-1 bg-zinc-800 rounded-lg appearance-none cursor-pointer" title="Doble click para resetear" />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <div className="flex justify-between text-[10px] uppercase text-zinc-500"><span>Horizontal (X)</span><span className="text-white font-mono">{posX}%</span></div>
                                                            <input type="range" min="0" max="100" step="1" value={posX} onChange={(e) => setPosX(parseInt(e.target.value))} onDoubleClick={() => setPosX(50)} className="w-full accent-white h-1 bg-zinc-800 rounded-lg appearance-none cursor-pointer" title="Doble click para resetear" />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <div className="flex justify-between text-[10px] uppercase text-zinc-500"><span>Vertical (Y)</span><span className="text-white font-mono">{posY}%</span></div>
                                                            <input type="range" min="0" max="100" step="1" value={posY} onChange={(e) => setPosY(parseInt(e.target.value))} onDoubleClick={() => setPosY(50)} className="w-full accent-white h-1 bg-zinc-800 rounded-lg appearance-none cursor-pointer" title="Doble click para resetear" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer Fixed */}
                        <div className="p-4 md:p-6 border-t border-zinc-800 bg-zinc-900/95 backdrop-blur-sm flex gap-3">
                            <button
                                onClick={() => setIsEditing(false)}
                                className="flex-1 px-4 py-3 border border-zinc-800 text-zinc-500 hover:text-white hover:bg-zinc-800 transition-all text-xs font-bold uppercase tracking-widest rounded-sm"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={uploading}
                                className="flex-[2] bg-white hover:bg-zinc-200 text-black font-bold py-3 uppercase rounded-sm flex justify-center items-center gap-2 transition-all shadow-[0_0_15px_rgba(255,255,255,0.2)] disabled:opacity-50"
                            >
                                {uploading ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                                {uploading ? 'Guardando...' : 'Guardar Banner'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isEditingSection && (
                <div
                    className="fixed inset-0 bg-black/70 backdrop-blur-3xl z-[99999] flex items-center justify-center p-4"
                    onClick={() => setIsEditingSection(false)}
                >
                    <div
                        className="bg-zinc-900 border border-zinc-800 w-full max-w-4xl rounded-lg shadow-2xl max-h-[90vh] flex flex-col relative z-[100000] overflow-hidden"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header Fixed */}
                        <div className="p-4 md:p-6 border-b border-zinc-800 flex justify-between items-center bg-zinc-900/95 backdrop-blur-sm z-10">
                            <h3 className="text-xl font-display font-bold uppercase italic text-white flex items-center gap-2">
                                <span className="text-white">{currentSection.id ? 'EDITAR' : 'NUEVA'}</span> SECCIÓN
                            </h3>
                            <button
                                onClick={() => setIsEditingSection(false)}
                                className="bg-zinc-800 hover:bg-white text-zinc-400 hover:text-black p-2 rounded-full transition-all border border-zinc-700 hover:border-white"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-grow overflow-y-auto p-6 space-y-6">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-[10px] font-bold uppercase text-zinc-500 mb-1 tracking-wider">Título de Marca</label>
                                    <input type="text" value={currentSection.title} onChange={e => setCurrentSection({ ...currentSection, title: e.target.value })} className="w-full bg-black border border-zinc-800 focus:border-white outline-none p-3 text-white rounded-sm transition-colors" placeholder="Ej: APPLE" />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold uppercase text-zinc-500 mb-1 tracking-wider">Subtítulo / Promo</label>
                                    <input type="text" value={currentSection.subtitle} onChange={e => setCurrentSection({ ...currentSection, subtitle: e.target.value })} className="w-full bg-black border border-zinc-800 focus:border-white outline-none p-3 text-white rounded-sm transition-colors" placeholder="Ej: Lo último del iPhone 15" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div><label className="block text-[10px] font-bold uppercase text-zinc-500 mb-1 tracking-wider">Orden</label><input type="number" value={currentSection.display_order} onChange={e => setCurrentSection({ ...currentSection, display_order: parseInt(e.target.value) })} className="w-full bg-black border border-zinc-800 focus:border-white outline-none p-3 text-white rounded-sm transition-colors" /></div>
                                    <div><label className="block text-[10px] font-bold uppercase text-zinc-500 mb-1 tracking-wider">Visibilidad</label><select value={currentSection.is_active ? 'true' : 'false'} onChange={e => setCurrentSection({ ...currentSection, is_active: e.target.value === 'true' })} className="w-full bg-black border border-zinc-800 focus:border-white outline-none p-3 text-white rounded-sm transition-colors"><option value="true">Activo</option><option value="false">Oculto</option></select></div>
                                </div>

                                <div>
                                    <label className="block text-[10px] font-bold uppercase text-zinc-500 mb-1 tracking-wider">Imagen de Sección</label>
                                    <div className="flex flex-col gap-2">
                                        <input type="file" accept="image/*" onChange={e => e.target.files && setSectionImageFile(e.target.files[0])} className="w-full bg-zinc-950 border border-zinc-800 p-2 text-xs text-zinc-400 file:bg-zinc-800 file:border-0 file:text-white file:px-3 file:py-1 file:rounded-sm file:mr-3 file:cursor-pointer" />

                                        {(currentSection.image_url || sectionImageFile) && (
                                            <div className="space-y-4">
                                                <div className="flex flex-wrap bg-black/40 p-1 rounded-lg border border-zinc-800 self-center justify-center gap-1 items-center">
                                                    {[
                                                        { id: 'mobile', icon: Smartphone, label: 'Móvil' },
                                                        { id: 'tablet', icon: Tablet, label: 'Tablet' },
                                                        { id: 'desktop', icon: Monitor, label: 'Escritorio' }
                                                    ].map((device) => (
                                                        <button
                                                            key={device.id}
                                                            onClick={() => changePreviewDevice(device.id as any)}
                                                            className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${previewDevice === device.id
                                                                ? 'bg-white text-black shadow-lg shadow-white/10'
                                                                : 'text-zinc-500 hover:text-white hover:bg-zinc-800'
                                                                }`}
                                                        >
                                                            <device.icon size={14} />
                                                            <span className="hidden sm:inline">{device.label}</span>
                                                        </button>
                                                    ))}
                                                    <div className="w-[1px] h-4 bg-zinc-800 mx-0.5 hidden sm:block" />
                                                    <button
                                                        onClick={() => setShowSafeZones(!showSafeZones)}
                                                        className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${showSafeZones
                                                            ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                                            : 'text-zinc-500 hover:text-white hover:bg-zinc-800'
                                                            }`}
                                                    >
                                                        <LayoutGrid size={14} />
                                                        <span className="hidden sm:inline">Guías</span>
                                                    </button>
                                                    <button
                                                        onClick={() => setShowRealPreview(!showRealPreview)}
                                                        className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${showRealPreview
                                                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                                            : 'text-zinc-500 hover:text-white hover:bg-zinc-800'
                                                            }`}
                                                        title="Ver con textos reales del catálogo"
                                                    >
                                                        <Zap size={14} />
                                                        <span className="hidden sm:inline">Real</span>
                                                    </button>
                                                </div>

                                                <div className="flex flex-col lg:flex-row gap-6 mt-4 items-start">
                                                    {/* Image Preview Side */}
                                                    <div className="w-full lg:w-2/3 flex flex-col space-y-2">
                                                        {/* Layout Context Indicator */}
                                                        <div className="flex justify-between items-center mb-2 px-1">
                                                            <span className="text-[9px] font-bold uppercase tracking-tighter text-zinc-500">
                                                                Simulación: {(currentSectionIndex % 6 === 2 || currentSectionIndex % 6 === 5) ? 'BANNER COMPLETO' : (currentSectionIndex % 6 === 0 || currentSectionIndex % 6 === 4) ? 'TARJETA DOBLE' : 'TARJETA SIMPLE'}
                                                            </span>
                                                            <span className="text-[9px] font-mono text-zinc-600">
                                                                Index: {currentSectionIndex}
                                                            </span>
                                                        </div>

                                                        <div className={`relative rounded overflow-hidden border border-zinc-700 bg-black group/preview transition-all duration-300 shadow-2xl mx-auto w-full ${previewDevice === 'mobile'
                                                            ? 'h-[400px] max-w-[280px]'
                                                            : (currentSectionIndex % 6 === 2 || currentSectionIndex % 6 === 5)
                                                                ? 'h-[250px] max-w-[800px]'
                                                                : (currentSectionIndex % 6 === 0 || currentSectionIndex % 6 === 4)
                                                                    ? 'h-[400px] max-w-[700px]'
                                                                    : 'h-[400px] max-w-[350px]'
                                                            }`}>
                                                            <img
                                                                src={sectionImageFile ? URL.createObjectURL(sectionImageFile) : currentSection.image_url}
                                                                className="w-full h-full object-cover transition-all duration-500"
                                                                style={{
                                                                    objectPosition: `${posX}% ${posY}%`,
                                                                    transform: `scale(${scale})`,
                                                                    transformOrigin: `${posX}% ${posY}%`
                                                                }}
                                                            />

                                                            {/* UNIVERSAL REAL CATALOG OVERLAY (Architectural Precision) */}
                                                            {showRealPreview && (
                                                                <div className="absolute inset-0 pointer-events-none z-20 overflow-hidden text-left">
                                                                    {(currentSectionIndex % 6 === 2 || currentSectionIndex % 6 === 5) ? (
                                                                        /* ACCESSORIES STYLE (Wide Banner) */
                                                                        <>
                                                                            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/60 to-transparent pointer-events-none opacity-95" />
                                                                            <div className="absolute inset-0 flex items-center justify-between px-6 md:px-10 z-20">
                                                                                <div className="max-w-[65%]">
                                                                                    <h3 className="text-xl md:text-3xl font-bold text-white tracking-tight mb-1">{currentSection.title || 'Marca'}</h3>
                                                                                    <p className="text-gray-300 text-[10px] md:text-sm">{currentSection.subtitle || 'Subtítulo del catálogo'}</p>
                                                                                </div>
                                                                                <div className="flex items-center justify-center w-12 h-12 md:w-16 md:h-16 bg-white/10 backdrop-blur rounded-full transition-colors p-2 shadow-2xl">
                                                                                    <img src="/CCSV_LOGO.svg" alt="Logo" className="w-[80%] h-[80%] object-contain filter drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]" />
                                                                                </div>
                                                                            </div>
                                                                        </>
                                                                    ) : (
                                                                        /* APPLE / SAMSUNG STYLE (Grid Cards) */
                                                                        <>
                                                                            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none opacity-95" />
                                                                            <div className="absolute top-6 left-6 right-6 z-20">
                                                                                <span className="text-white/60 font-bold uppercase tracking-widest text-[8px] md:text-[9px] mb-1 md:mb-2 block">Colección Premium</span>
                                                                                <h3 className="text-2xl md:text-3xl font-bold text-white tracking-tight leading-tight">{currentSection.title || 'Marca'}</h3>
                                                                                <p className="text-gray-300 mt-1 md:mt-2 text-[10px] md:text-sm line-clamp-2">{currentSection.subtitle || 'Subtítulo del catálogo'}</p>
                                                                            </div>
                                                                            <div className="absolute bottom-6 left-6 z-20">
                                                                                <span className="inline-flex items-center gap-2 bg-white text-black px-4 py-2 rounded-full font-bold text-[9px] uppercase tracking-wide shadow-[0_0_20px_rgba(255,255,255,0.3)]">
                                                                                    {currentSection.cta_text || 'Ver Catálogo'}
                                                                                </span>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            )}

                                                            {/* ARCHITECTURAL SAFE ZONES (Percentage based on Exact Pixels) */}
                                                            {showSafeZones && (
                                                                <div className="absolute inset-0 pointer-events-none z-30">
                                                                    {(currentSectionIndex % 6 === 2 || currentSectionIndex % 6 === 5) ? (
                                                                        /* Banner Pattern */
                                                                        <div className="absolute flex items-center justify-between" style={{ top: '15%', bottom: '15%', left: '8%', right: '8%' }}>
                                                                            <div className="w-[65%] h-[80%] border border-white/80 opacity-90 border-dashed bg-white/5" />
                                                                            <div className="w-12 h-12 md:w-16 md:h-16 border border-white/80 opacity-90 border-dashed rounded-full bg-white/5" />
                                                                        </div>
                                                                    ) : (
                                                                        /* Card Pattern */
                                                                        <>
                                                                            <div className="absolute border border-white/80 opacity-95 border-dashed bg-white/5"
                                                                                style={{ top: '6%', left: '6%', right: '6%', height: '35%' }} />
                                                                            <div className="absolute border border-white/80 opacity-95 border-dashed bg-white/5"
                                                                                style={{ bottom: '6%', left: '6%', width: '35%', height: '12%' }} />
                                                                        </>
                                                                    )}

                                                                    {/* Precision Center Crosshair - Architect Standard */}
                                                                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-[1.5px] bg-white shadow-sm" />
                                                                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-16 w-[1.5px] bg-white shadow-sm" />
                                                                </div>
                                                            )}

                                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/preview:opacity-100 transition-opacity flex items-center justify-center pointer-events-none text-white font-bold uppercase tracking-widest text-[9px] backdrop-blur-[2px]">
                                                                <span>Espejo Arquitectónico: {(currentSectionIndex % 6 === 2 || currentSectionIndex % 6 === 5) ? 'Accesorios' : (currentSectionIndex % 6 === 0 || currentSectionIndex % 6 === 4) ? 'Apple (1.8:1)' : 'Samsung (0.87:1)'}</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Controls Side */}
                                                    <div className="w-full lg:w-1/3 bg-zinc-950 p-4 rounded border border-zinc-800 space-y-4 shadow-inner shrink-0 sticky top-0">
                                                        <h4 className="text-[10px] font-bold uppercase text-zinc-500 mb-2 flex items-center gap-2">
                                                            <Move size={12} /> Ajustes de Encuadre
                                                        </h4>
                                                        <div className="space-y-1">
                                                            <div className="flex justify-between text-[10px] uppercase text-zinc-500"><span>Zoom</span><span className="text-white font-mono">{scale.toFixed(1)}x</span></div>
                                                            <input type="range" min="1" max="2" step="0.1" value={scale} onChange={(e) => setScale(parseFloat(e.target.value))} onDoubleClick={() => setScale(1.0)} className="w-full accent-white h-1 bg-zinc-800 rounded-lg appearance-none cursor-pointer" title="Doble click para resetear" />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <div className="flex justify-between text-[10px] uppercase text-zinc-500"><span>Horizontal (X)</span><span className="text-white font-mono">{posX}%</span></div>
                                                            <input type="range" min="0" max="100" step="1" value={posX} onChange={(e) => setPosX(parseInt(e.target.value))} onDoubleClick={() => setPosX(50)} className="w-full accent-white h-1 bg-zinc-800 rounded-lg appearance-none cursor-pointer" title="Doble click para resetear" />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <div className="flex justify-between text-[10px] uppercase text-zinc-500"><span>Vertical (Y)</span><span className="text-white font-mono">{posY}%</span></div>
                                                            <input type="range" min="0" max="100" step="1" value={posY} onChange={(e) => setPosY(parseInt(e.target.value))} onDoubleClick={() => setPosY(50)} className="w-full accent-white h-1 bg-zinc-800 rounded-lg appearance-none cursor-pointer" title="Doble click para resetear" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer Fixed */}
                        <div className="p-4 md:p-6 border-t border-zinc-800 bg-zinc-900/95 backdrop-blur-sm flex gap-3">
                            <button
                                onClick={() => setIsEditingSection(false)}
                                className="flex-1 px-4 py-3 border border-zinc-800 text-zinc-500 hover:text-white hover:bg-zinc-800 transition-all text-xs font-bold uppercase tracking-widest rounded-sm"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSaveSection}
                                disabled={uploading}
                                className="flex-[2] bg-white hover:bg-zinc-200 text-black font-bold py-3 uppercase rounded-sm flex justify-center items-center gap-2 transition-all shadow-[0_0_15px_rgba(255,255,255,0.2)]"
                            >
                                {uploading ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                                {uploading ? 'Guardando...' : 'Guardar Sección'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isEditingFamily && (
                <EditFamilyModal
                    family={currentFamily as DeviceFamily}
                    brands={brands}
                    isOpen={isEditingFamily}
                    onClose={() => setIsEditingFamily(false)}
                    onSave={handleSaveFamily}
                />
            )}

            <div className="space-y-6 mb-8">
                <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-display font-bold uppercase italic">Gestión de Contenido</h2>
                </div>

                <div className="flex gap-4 border-b border-zinc-800 pb-px overflow-x-auto hide-scrollbar">
                    <button
                        type="button"
                        onClick={() => setActiveTab('banners')}
                        className={`px-4 py-3 font-bold uppercase tracking-widest flex items-center gap-2 text-xs border-b-2 transition-all whitespace-nowrap ${activeTab === 'banners' ? 'border-white text-white bg-white/5' : 'border-transparent text-zinc-500 hover:text-white hover:border-zinc-700 hover:bg-white/5'}`}
                    >
                        <ImageIcon size={16} /> Hero Banners
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('sections')}
                        className={`px-4 py-3 font-bold uppercase tracking-widest flex items-center gap-2 text-xs border-b-2 transition-all whitespace-nowrap ${activeTab === 'sections' ? 'border-white text-white bg-white/5' : 'border-transparent text-zinc-500 hover:text-white hover:border-zinc-700 hover:bg-white/5'}`}
                    >
                        <LayoutGrid size={16} /> Secciones Destacadas
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('families')}
                        className={`px-4 py-3 font-bold uppercase tracking-widest flex items-center gap-2 text-xs border-b-2 transition-all whitespace-nowrap ${activeTab === 'families' ? 'border-white text-white bg-white/5' : 'border-transparent text-zinc-500 hover:text-white hover:border-zinc-700 hover:bg-white/5'}`}
                    >
                        <Smartphone size={16} /> Familias de Dispositivos
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('narrative')}
                        className={`px-4 py-3 font-bold uppercase tracking-widest flex items-center gap-2 text-xs border-b-2 transition-all whitespace-nowrap ${activeTab === 'narrative' ? 'border-white text-white bg-white/5' : 'border-transparent text-zinc-500 hover:text-white hover:border-zinc-700 hover:bg-white/5'}`}
                    >
                        <LayoutGrid size={16} /> Experiencia
                    </button>
                </div>
            </div>

            {/* --- BANNERS SECTION --- */}
            {activeTab === 'banners' && (
                <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-bold uppercase tracking-widest flex items-center gap-2">
                            <ImageIcon size={20} className="text-zinc-500" />
                            Hero Banners
                        </h3>
                        <button
                            onClick={handleAddNew}
                            className="bg-white hover:bg-zinc-200 text-black font-bold py-2 px-4 rounded-sm flex items-center gap-2 transition-colors text-xs uppercase"
                        >
                            <Plus size={16} /> Nuevo Banner
                        </button>
                    </div>

                    <div className="bg-zinc-900/30 border border-zinc-800 rounded-sm overflow-hidden p-6">
                        {isLoading ? (
                            <div className="text-center py-10 text-zinc-500">Cargando banners...</div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {banners.map((banner) => (
                                    <div key={banner.id} className="group relative bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden transition-all hover:border-zinc-600">
                                        <div className="aspect-video relative overflow-hidden">
                                            {banner.image?.match(/\.(mp4|webm|ogg)$/i) ? (
                                                <video
                                                    src={banner.image}
                                                    className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity"
                                                    style={{
                                                        objectPosition: banner.position || 'center',
                                                        transform: `scale(${banner.scale || 1})`,
                                                        transformOrigin: banner.position || 'center'
                                                    }}
                                                    muted loop
                                                />
                                            ) : (
                                                <img
                                                    src={banner.image}
                                                    alt={banner.title}
                                                    className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity"
                                                    style={{
                                                        objectPosition: banner.position || 'center',
                                                        transform: `scale(${banner.scale || 1})`,
                                                        transformOrigin: banner.position || 'center'
                                                    }}
                                                />
                                            )}
                                            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent pointer-events-none" />
                                            <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
                                                <h4 className="font-display font-black text-white text-lg uppercase italic leading-none mb-1 drop-shadow-lg line-clamp-1">
                                                    {banner.title || '(Sin Título)'}
                                                </h4>
                                                {banner.subtitle && (
                                                    <p className="text-xs text-zinc-300 font-medium drop-shadow-md line-clamp-1">{banner.subtitle}</p>
                                                )}
                                            </div>
                                            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => handleEdit(banner)}
                                                    className="p-1.5 bg-black/50 hover:bg-white text-white hover:text-black rounded backdrop-blur-sm transition-colors"
                                                >
                                                    <Edit size={14} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(banner.id)}
                                                    className="p-1.5 bg-black/50 hover:bg-red-500 text-white rounded backdrop-blur-sm transition-colors"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="p-3 flex justify-between items-center text-[10px] font-mono uppercase bg-zinc-950/50">
                                            <span className={banner.active ? 'text-emerald-500' : 'text-red-500'}>
                                                {banner.active ? '● Activo' : '○ Inactivo'}
                                            </span>
                                            <span className="text-zinc-500">Orden: {banner.order}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

            )}

            {/* --- SECTIONS SECTION --- */}
            {activeTab === 'sections' && (
                <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-bold uppercase tracking-widest flex items-center gap-2">
                            <LayoutGrid size={20} className="text-zinc-500" />
                            Secciones Destacadas (Home)
                        </h3>
                        <button
                            onClick={handleAddNewSection}
                            className="bg-white hover:bg-zinc-200 text-black font-bold py-2 px-4 rounded-sm flex items-center gap-2 transition-colors text-xs uppercase"
                        >
                            <Plus size={16} /> Nueva Sección
                        </button>
                    </div>

                    <div className="bg-zinc-900/30 border border-zinc-800 rounded-sm overflow-hidden p-6">
                        {isLoading ? (
                            <div className="text-center py-10 text-zinc-500">Cargando secciones...</div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {sections.map((section) => (
                                    <div key={section.key} className="group relative bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden transition-all hover:border-zinc-600">
                                        <div className="aspect-[4/3] relative overflow-hidden">
                                            <img
                                                src={section.image_url}
                                                alt={section.title}
                                                className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity"
                                                style={{
                                                    objectPosition: section.position || 'center',
                                                    transform: `scale(${section.scale || 1})`,
                                                    transformOrigin: section.position || 'center'
                                                }}
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent pointer-events-none" />
                                            <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
                                                <span className="text-[10px] font-mono uppercase text-white mb-1 block">{section.key}</span>
                                                <h4 className="font-display font-black text-white text-2xl uppercase italic leading-none mb-1 drop-shadow-lg">
                                                    {section.title}
                                                </h4>
                                                <p className="text-xs text-zinc-300 font-medium drop-shadow-md line-clamp-2">{section.subtitle}</p>
                                            </div>
                                            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => handleEditSection(section)}
                                                    className="p-1.5 bg-black/50 hover:bg-white text-white hover:text-black rounded backdrop-blur-sm transition-colors"
                                                >
                                                    <Edit size={14} />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteSection(section.id)}
                                                    className="p-1.5 bg-black/50 hover:bg-red-500 text-white rounded backdrop-blur-sm transition-colors"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="p-3 flex justify-between items-center text-[10px] font-mono uppercase bg-zinc-950/50">
                                            <span className={section.is_active ? 'text-emerald-500' : 'text-red-500'}>
                                                {section.is_active ? '● Activo' : '○ Inactivo'}
                                            </span>
                                            <span className="text-zinc-500">Orden: {section.display_order}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>


            )}

            {/* --- FAMILIES SECTION --- */}
            {activeTab === 'families' && (
                <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-bold uppercase tracking-widest flex items-center gap-2">
                            <LayoutGrid size={20} className="text-zinc-500" />
                            Familias de Dispositivos (Menú)
                        </h3>
                        <button
                            onClick={() => {
                                setCurrentFamily({});
                                setIsEditingFamily(true);
                            }}
                            className="bg-white hover:bg-zinc-200 text-black font-bold py-2 px-4 rounded-sm flex items-center gap-2 transition-colors text-xs uppercase"
                        >
                            <Plus size={16} /> Nueva Familia
                        </button>
                    </div>

                    <div className="bg-zinc-900/30 border border-zinc-800 rounded-sm overflow-hidden p-6 space-y-10">
                        {isLoading ? (
                            <div className="text-center py-10 text-zinc-500">Cargando familias...</div>
                        ) : (
                            <>
                                {brands.map(brand => {
                                    const brandFamilies = families.filter(f => f.brand_id === brand.id);
                                    if (brandFamilies.length === 0) return null;

                                    return (
                                        <div key={brand.id}>
                                            <h4 className="text-zinc-500 font-bold uppercase tracking-widest text-xs mb-4 border-b border-zinc-800 pb-2">
                                                {brand.name}
                                            </h4>
                                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
                                                {brandFamilies.map((family) => (
                                                    <div key={family.id} className="group relative bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden transition-all hover:border-zinc-600">
                                                        <div className="aspect-[4/5] relative overflow-hidden bg-black">
                                                            {family.image_url ? (
                                                                <img
                                                                    src={family.image_url}
                                                                    alt={family.name}
                                                                    className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity"
                                                                />
                                                            ) : (
                                                                <div className="w-full h-full flex items-center justify-center text-zinc-700 font-mono text-xs">Sin Imagen</div>
                                                            )}
                                                            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent pointer-events-none" />
                                                            <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
                                                                <h4 className="font-display font-bold text-white text-sm uppercase leading-tight mb-1 drop-shadow-lg">
                                                                    {family.name}
                                                                </h4>
                                                            </div>
                                                            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <button
                                                                    onClick={() => handleEditFamily(family)}
                                                                    className="p-1.5 bg-black/50 hover:bg-white text-white hover:text-black rounded backdrop-blur-sm transition-colors"
                                                                >
                                                                    <Edit size={14} />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteFamily(family.id)}
                                                                    className="p-1.5 bg-black/50 hover:bg-red-500 text-white rounded backdrop-blur-sm transition-colors"
                                                                    title="Eliminar Familia"
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div className="p-2 flex justify-between items-center text-[9px] font-mono uppercase bg-zinc-950/50">
                                                            <span className={family.is_active ? 'text-emerald-500' : 'text-red-500'}>
                                                                {family.is_active ? '● Activo' : '○ Inactivo'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* --- NARRATIVE SECTION --- */}
            {activeTab === 'narrative' && (
                <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <NarrativeSettings />
                </div>
            )}
        </div >

    );
};

export default ContentPage;
